import axios from '@/plugins/axios'
import { COLLECTION_ITEMS, USER_DELETED_ITEMS } from '@/api/utils/endpoints'

// Helper to build base URL for collection items
const itemsUrl = (collectionId) => `${COLLECTION_ITEMS}/${collectionId}/items`

const getRelationId = value => value?._id || value || null

const normalizeCollectionItemTags = tags =>
  (tags || [])
    .filter(tag => tag && typeof tag === 'object')
    .map(tag => ({ ...tag, ownerId: getRelationId(tag.owner) }))

const normalizeCollectionItem = (item) => {
  const { tags: rawTags, ...rest } = item
  const tags = normalizeCollectionItemTags(rawTags)

  return {
    ...rest,
    ownerId: getRelationId(item.owner) ?? item.ownerId,
    addedById: getRelationId(item.addedBy) ?? item.addedById ?? null,
    tagIds: item.tagIds?.length
      ? item.tagIds
      : (rawTags?.map(getRelationId).filter(Boolean) || []),
    tags,
  }
}

const normalizeCollectionItems = items => items.map(normalizeCollectionItem)

// ==================== LIST / GET ====================

export const get = async (collectionId, ...o) => {
  const { results, next, itemCount } = await axios.get(itemsUrl(collectionId), ...o)
  return {
    results: normalizeCollectionItems(results),
    next,
    itemCount,
  }
}

export const getPinned = async (collectionId, ...o) => {
  const { results, next, itemCount } = await axios.get(`${itemsUrl(collectionId)}/pinned`, ...o)
  return {
    results: normalizeCollectionItems(results),
    next,
    itemCount,
  }
}

export const getId = async (collectionId, itemId, ...o) => {
  const item = await axios.get(`${itemsUrl(collectionId)}/${itemId}`, ...o)
  return normalizeCollectionItem(item)
}

// ==================== STATS ====================

export const stats = (collectionId, ...o) => axios.get(`${itemsUrl(collectionId)}/stats`, ...o)

// ==================== CREATE ====================

export const createBookmark = (collectionId, data, ...o) => axios.post(`${itemsUrl(collectionId)}/bookmarks`, data, ...o)

export const createBookmarkExtension = (collectionId, data, ...o) => axios.post(`${itemsUrl(collectionId)}/bookmarks/extension`, data, ...o)

export const createNote = (collectionId, data, ...o) => axios.post(`${itemsUrl(collectionId)}/notes`, data, ...o)

// ==================== UPDATE ====================

export const edit = (collectionId, itemId, data, ...o) => axios.patch(`${itemsUrl(collectionId)}/${itemId}`, data, ...o)

export const editTags = (collectionId, itemId, data, ...o) => axios.patch(`${itemsUrl(collectionId)}/${itemId}/tags`, data, ...o)

export const editThumbnail = (collectionId, itemId, file, opts) => {
  const formData = new FormData()
  formData.append('file', file)
  return axios.post(
    `${itemsUrl(collectionId)}/${itemId}/image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, ...opts },
  )
}

// ==================== DELETE / RESTORE ====================

export const remove = (collectionId, itemId, ...o) => axios.delete(`${itemsUrl(collectionId)}/${itemId}`, ...o)

export const removeFull = (collectionId, itemId, ...o) => axios.delete(`${itemsUrl(collectionId)}/${itemId}/full`, ...o)

export const restore = (collectionId, itemId, ...o) => axios.post(`${itemsUrl(collectionId)}/${itemId}/restore`, ...o)

// ==================== UNIFIED TRASH (global, cross-collection) ====================

// Global deleted list scoped to deletedBy = current user (+ own private collection).
export const getDeletedItems = async (...o) => {
  const { results, next } = await axios.get(USER_DELETED_ITEMS, ...o)
  return {
    results: normalizeCollectionItems(results),
    next,
  }
}

// Restore single or many: backend groups by target collection, creates collections as
// needed, per-group atomic. `forceNewCollectionItemIds` opts items into a new collection.
export const restoreDeletedItems = (data, ...o) => axios.post(`${USER_DELETED_ITEMS}/restore`, data, ...o)

// ==================== COLLECTIONS ====================

export const addToCollections = (collectionId, itemId, data, ...o) => axios.post(`${itemsUrl(collectionId)}/${itemId}/add-collections`, data, ...o)

// ==================== IMPORT / EXPORT ====================

export const importItems = (collectionId, file, ...o) => {
  const formData = new FormData()
  formData.append('file', file)
  return axios.post(`${itemsUrl(collectionId)}/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, ...o })
}

export const exportItems = (collectionId, form, ...o) => axios.post(`${itemsUrl(collectionId)}/export`, form, ...o)

// ==================== TAGS ====================

export const searchTags = async (collectionId, opts) => {
  const { results, next } = await axios.get(`${itemsUrl(collectionId)}/tags`, opts)
  return {
    results: results.map(i => ({ ...i, ownerId: i.owner })),
    next,
  }
}

export const getTagsById = async (collectionId, opts) => {
  const tags = await axios.get(`${itemsUrl(collectionId)}/tags/ids`, opts)
  return tags.map(i => ({ ...i, ownerId: i.owner }))
}

export const popularTags = async (collectionId, opts) => {
  const results = await axios.get(`${itemsUrl(collectionId)}/tags/popular`, opts)
  return results.map(i => ({ ...i, ownerId: i.owner }))
}

// ==================== COMMENTS ====================

export const comments = async (collectionId, itemId, opts) => {
  const { results, next } = await axios.get(`${itemsUrl(collectionId)}/${itemId}/comments`, opts)
  return {
    results: results.map(i => ({
      ...i,
      userId: i.user._id,
      resourceId: itemId,
      resourceType: 'collectionitems',
    })),
    next,
  }
}

export const comment = (collectionId, itemId, data) => axios
  .post(`${itemsUrl(collectionId)}/${itemId}/comments`, data)
  .then(i => ({
    ...i,
    userId: i.user,
    resourceId: itemId,
    resourceType: 'collectionitems',
  }))

export const removeComment = (collectionId, itemId, commentId) => axios.delete(`${itemsUrl(collectionId)}/${itemId}/comments/${commentId}`)

export const setCommentsSeen = (collectionId, itemId) => axios.post(`${itemsUrl(collectionId)}/${itemId}/comments/seen`)

// ==================== LIKES ====================

export const likes = async (collectionId, itemId, opts) => {
  const { results, next } = await axios.get(`${itemsUrl(collectionId)}/${itemId}/likes`, opts)
  return {
    results: results.map(i => ({
      ...i,
      userId: i.user._id,
      resourceId: itemId,
      resourceType: 'collectionitems',
    })),
    next,
  }
}

export const like = (collectionId, itemId, data) => axios
  .post(`${itemsUrl(collectionId)}/${itemId}/likes`, data)
  .then(i => ({
    ...i,
    userId: i.user,
    resourceId: itemId,
    resourceType: 'collectionitems',
  }))

export const removeLike = (collectionId, itemId) => axios.delete(`${itemsUrl(collectionId)}/${itemId}/likes`)

// ==================== PIN ====================

export const pinItem = async (collectionId, itemId, data, ...o) => {
  const item = await axios.post(`${itemsUrl(collectionId)}/${itemId}/pin`, data, ...o)
  return normalizeCollectionItem(item)
}

export const unpinItem = (collectionId, itemId, ...o) => axios.delete(`${itemsUrl(collectionId)}/${itemId}/pin`, ...o)

// ==================== BULK OPERATIONS (V2) ====================

export const favoriteBulk = (collectionId, data, ...o) => axios.post(`${itemsUrl(collectionId)}/bulk/favorite`, data, ...o)

export const removeBulk = (collectionId, data, ...o) => axios.post(`${itemsUrl(collectionId)}/bulk/delete`, data, ...o)

export const removeFullBulk = (collectionId, data, ...o) => axios.post(`${itemsUrl(collectionId)}/bulk/delete-full`, data, ...o)

export const restoreBulk = (collectionId, data, ...o) => axios.post(`${itemsUrl(collectionId)}/bulk/restore`, data, ...o)

export const addTagsBulk = (collectionId, data, ...o) => axios.post(`${itemsUrl(collectionId)}/bulk/add-tags`, data, ...o)

export const removeTagsBulk = (collectionId, data, ...o) => axios.post(`${itemsUrl(collectionId)}/bulk/remove-tags`, data, ...o)

export const addToCollectionsBulk = (collectionId, data, ...o) => axios.post(`${itemsUrl(collectionId)}/bulk/add-collections`, data, ...o)

export const getBulkTags = async (collectionId, data, ...o) => {
  const { results, next } = await axios.post(`${itemsUrl(collectionId)}/bulk/get-tags`, data, ...o)
  const tags = results.map(i => ({ ...i, ownerId: i.owner }))
  return {
    results: tags,
    next,
  }
}
